export interface Subject {
  id: string;
  name: string;
  category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
  onlineRate: number;
  inPersonRate: number;
  isActive: boolean;
}

export interface StudentHourPackage {
  id: string;
  studentId: string;
  subjectId: string;
  totalHours: number;
  usedHours: number;
  remainingHours: number;
  packageType: HourPackageType;
  purchasePrice: number;
  pricePerHour: number;
  discountPercent: number;
  isOnline: boolean;
  status: HourPackageStatus;
  purchasedAt: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  subject: Subject;
  transactions?: PackageTransaction[];
}

export interface PackageTransaction {
  id: string;
  packageId: string;
  bookingId?: string;
  transactionType: PackageTransactionType;
  hoursChanged: number;
  description?: string;
  performedBy?: string;
  createdAt: string;
  // Relations
  package?: StudentHourPackage;
  booking?: any; // Booking type would be defined elsewhere
  admin?: any; // User type would be defined elsewhere
}

export interface PackageOption {
  type: HourPackageType;
  name?: string;
  hours: number;
  discountPercent: number;
  originalPrice: number;
  discountedPrice: number;
  discountAmount: number;
}

export interface PackagePricingResponse {
  subject: {
    id: string;
    name: string;
    category: 'ELEVEN_PLUS' | 'GCSE' | 'A_LEVEL' | 'UNIVERSITY';
    rate: number;
  };
  packages: PackageOption[];
  isOnline: boolean;
}

export interface PackagePurchaseRequest {
  subjectId: string;
  packageType: HourPackageType;
  isOnline: boolean;
}

export interface PackagePurchaseResponse {
  message: string;
  package: StudentHourPackage;
  pricing: {
    originalPrice: number;
    discountedPrice: number;
    discountAmount: number;
  };
  bankDetails: {
    accountName: string;
    sortCode: string;
    accountNumber: string;
    reference: string;
  };
}

export interface HourAdjustmentRequest {
  hoursChange: number;
  description: string;
}

export interface AdminPackageAssignRequest {
  studentId: string;
  subjectId: string;
  packageType: HourPackageType;
  isOnline: boolean;
}

export interface AdminPackageAssignResponse {
  message: string;
  package: StudentHourPackage;
  pricing: {
    originalPrice: number;
    discountedPrice: number;
    discountAmount: number;
  };
}

// `HourPackageType` is now a free-form identifier (admin-defined). Legacy
// values "TEN_HOUR" / "TWENTY_HOUR" / "THIRTY_HOUR" stay valid; new custom
// packages get slug identifiers like "custom_50h_abc123".
export type HourPackageType = string;

export type HourPackageStatus = 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'DEPLETED' | 'REFUNDED';

export type PackageTransactionType = 'PURCHASE' | 'USAGE' | 'ADMIN_ADJUSTMENT' | 'REFUND';

// Utility functions
export const formatPackageType = (type: HourPackageType, hoursOverride?: number): string => {
  if (hoursOverride !== undefined) return `${hoursOverride} Hour Package`;
  switch (type) {
    case 'TEN_HOUR':
      return '10 Hour Package';
    case 'TWENTY_HOUR':
      return '20 Hour Package';
    case 'THIRTY_HOUR':
      return '30 Hour Package';
    default:
      return type;
  }
};

export const getPackageStatusColor = (status: HourPackageStatus): string => {
  switch (status) {
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-800';
    case 'ACTIVE':
      return 'bg-green-100 text-green-800';
    case 'EXPIRED':
      return 'bg-yellow-100 text-yellow-800';
    case 'DEPLETED':
      return 'bg-gray-100 text-gray-800';
    case 'REFUNDED':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getDiscountForPackage = (type: HourPackageType): number => {
  switch (type) {
    case 'TEN_HOUR':
      return 10;
    case 'TWENTY_HOUR':
      return 15;
    case 'THIRTY_HOUR':
      return 20;
    default:
      return 0;
  }
};

export const getHoursForPackage = (type: HourPackageType): number => {
  switch (type) {
    case 'TEN_HOUR':
      return 10;
    case 'TWENTY_HOUR':
      return 20;
    case 'THIRTY_HOUR':
      return 30;
    default:
      return 0;
  }
};